import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, Send, Sparkles, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'lafayette_ai_session';

const generateSessionId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const useSessionId = () => {
  const [sessionId] = useState(() => {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = generateSessionId();
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  });
  return sessionId;
};

export const AIAssistantModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const sessionId = useSessionId();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // First-time greeting
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', content: t('aiAssistant.greeting') }]);
    }
  }, [open, messages.length, t]);

  // Auto-scroll to newest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when modal opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]);

  // Body scroll lock + ESC to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const sendMessage = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || loading) return;
    setError(null);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setError(err.message || t('aiAssistant.error'));
    } finally {
      setLoading(false);
    }
  };

  const suggestions = t('aiAssistant.suggestions', { returnObjects: true });
  const showSuggestions = messages.filter((m) => m.role === 'user').length === 0 && !loading;
  const currentLang = i18n.resolvedLanguage || 'fr';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="ai-assistant-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-2xl h-[92vh] sm:h-[85vh] bg-white sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-slate-900" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-playfair font-bold text-white text-base sm:text-lg leading-tight">
                    {t('aiAssistant.title')}
                  </h3>
                  <p className="font-inter text-[11px] text-slate-300 leading-tight">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 align-middle" />
                    {t('aiAssistant.subtitle')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="ai-modal-close"
                aria-label={t('aiAssistant.close')}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 sm:px-5 py-5 space-y-4 bg-slate-50"
              data-testid="ai-messages"
            >
              {messages.map((m, idx) => (
                <div
                  key={`${m.role}-${idx}-${m.content.slice(0, 12)}`}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    data-testid={`ai-message-${m.role}`}
                    className={`max-w-[85%] sm:max-w-[80%] rounded-2xl px-4 py-2.5 font-inter text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-slate-900 text-white rounded-br-sm'
                        : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start" data-testid="ai-typing-indicator">
                  <div className="bg-white text-slate-500 border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                  </div>
                </div>
              )}

              {error && (
                <div className="text-center text-xs font-inter text-red-600 bg-red-50 border border-red-200 rounded-lg py-2 px-3">
                  {error}
                </div>
              )}

              {showSuggestions && Array.isArray(suggestions) && suggestions.length > 0 && (
                <div className="pt-2">
                  <p className="font-inter text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    {t('aiAssistant.suggestionsTitle')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => sendMessage(q)}
                        data-testid="ai-suggestion-chip"
                        className="text-left font-inter text-xs sm:text-sm bg-white hover:bg-amber-50 text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-amber-300 rounded-full px-3.5 py-1.5 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage();
              }}
              className="flex items-end gap-2 px-3 sm:px-4 py-3 border-t border-slate-200 bg-white"
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                data-testid="ai-input"
                placeholder={t('aiAssistant.placeholder')}
                maxLength={2000}
                rows={1}
                lang={currentLang}
                className="flex-1 resize-none font-inter text-sm bg-slate-100 focus:bg-white text-slate-900 placeholder-slate-400 border border-slate-200 focus:border-amber-400 rounded-2xl px-4 py-3 outline-none max-h-32 transition-colors"
                style={{ minHeight: '48px' }}
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                data-testid="ai-send-btn"
                aria-label={t('aiAssistant.send')}
                className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-slate-900 font-semibold transition-colors flex-shrink-0"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </form>

            {/* Legal footer */}
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
              <p className="font-inter text-[10px] text-slate-500 text-center leading-tight">
                {t('aiAssistant.disclaimer')}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
